/**
 * FeedbackTab.tsx - Feedback & Bug Reports tab for Admin Dashboard
 *
 * Displays submitted feedback from the feedbackReports Firestore collection
 * via the admin-only GET /api/getFeedbackReports endpoint.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './FeedbackTab.css';

interface FeedbackReport {
  id: string;
  uid: string | null;
  email: string | null;
  category: string;
  message: string;
  status: string;
  hasScreenshot: boolean;
  hasConsoleLogs: boolean;
  deviceInfo: {
    userAgent: string;
    language: string;
    languages?: string[];
    platform: string;
    screenWidth: number;
    screenHeight: number;
    viewportWidth?: number;
    viewportHeight?: number;
    url: string;
    currentRoute?: string;
    timestamp: string;
    appVersion?: string;
    gitCommit?: string;
    buildTimestamp?: string;
    theme?: string;
    online?: boolean;
    connectionType?: string | null;
    environment?: string | null;
    apiBaseUrl?: string | null;
    authProvider?: string | null;
    accountCreated?: string | null;
    locale?: string | null;
    timezone?: string | null;
  } | null;
  createdAt: string | null;
}

interface ApiCallEntry {
  ts: string;
  method: string;
  url: string;
  status: number | null;
  durationMs: number;
  error?: string;
}

interface FeedbackReportDetail {
  id: string;
  consoleLogs: string[];
  recentApiCalls: ApiCallEntry[];
  usageSnapshot: Record<string, unknown> | null;
  screenshot: string | null;
  recentMessages: { sender: string; text: string; timestamp?: string }[];
}

const CATEGORY_ICONS: Record<string, string> = {
  bug: '🐛',
  question: '❓',
  payment: '💳',
  feature: '💡',
  other: '📝',
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'status-new' },
  'in-progress': { label: 'In Progress', className: 'status-progress' },
  resolved: { label: 'Resolved', className: 'status-resolved' },
  dismissed: { label: 'Dismissed', className: 'status-dismissed' },
};

interface FeedbackTabProps {
  language?: string;
}

const FeedbackTab: React.FC<FeedbackTabProps> = ({ language }) => {
  const { t } = useTranslation('admin', { lng: language });
  const [reports, setReports] = useState<FeedbackReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, FeedbackReportDetail>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());
  const detailInflight = useRef(new Set<string>());
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const loadDetail = useCallback(async (id: string) => {
    if (details[id] || detailInflight.current.has(id)) return; // already loaded or in-flight
    detailInflight.current.add(id);
    setDetailLoading(id);
    try {
      const res = await authFetch(`${getApiBaseUrl()}/getFeedbackReport/${id}`);
      const json = await res.json();
      if (res.ok && json.success) {
        setDetails(prev => ({ ...prev, [id]: json.data }));
      }
    } catch {
      // detail load failure is non-critical
    } finally {
      detailInflight.current.delete(id);
      setDetailLoading(prev => (prev === id ? null : prev));
    }
  }, [details]);

  const loadReports = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (cursor) params.set('cursor', cursor);
      params.set('limit', '50');

      const res = await authFetch(`${getApiBaseUrl()}/getFeedbackReports?${params}`);

      let json: Record<string, unknown> | null = null;
      try {
        json = await res.json();
      } catch {
        // non-JSON response
      }

      if (!res.ok) {
        const msg = json && typeof json.error === 'string'
          ? json.error
          : `Request failed with status ${res.status}`;
        throw new Error(msg);
      }

      if (!json || typeof json !== 'object' || !json.success) {
        throw new Error((json && typeof json.error === 'string' ? json.error : null) || 'Failed to load');
      }

      if (cursor) {
        setReports(prev => [...prev, ...(json as Record<string, unknown>).data as FeedbackReport[]]);
      } else {
        setReports((json as Record<string, unknown>).data as FeedbackReport[]);
      }
      setHasMore((json as Record<string, unknown>).hasMore as boolean);
      setNextCursor((json as Record<string, unknown>).nextCursor as string | null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const toggleExpand = (id: string) => {
    const willExpand = expanded !== id;
    setExpanded(prev => prev === id ? null : id);
    if (willExpand) loadDetail(id);
  };

  const updateStatus = async (id: string, newStatus: string) => {
    setActionLoading(prev => new Set(prev).add(id));
    try {
      const res = await authFetch(`${getApiBaseUrl()}/feedbackReport/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setReports(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
      }
    } catch {
      // non-critical
    } finally {
      setActionLoading(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const deleteReport = async (id: string) => {
    if (!window.confirm('Permanently delete this feedback report?')) return;
    setActionLoading(prev => new Set(prev).add(id));
    try {
      const res = await authFetch(`${getApiBaseUrl()}/feedbackReport/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setReports(prev => prev.filter(r => r.id !== id));
        if (expanded === id) setExpanded(null);
      }
    } catch {
      // non-critical
    } finally {
      setActionLoading(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  return (
    <div className="feedback-tab">
      <div className="feedback-tab__header">
        <h2>{t('feedback.title', 'Feedback & Bug Reports')}</h2>
        <div className="feedback-tab__filters">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="feedback-tab__select"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <button className="feedback-tab__refresh" onClick={() => loadReports()}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && <div className="feedback-tab__error">⚠️ {error}</div>}

      {!loading && reports.length === 0 && (
        <div className="feedback-tab__empty">
          No feedback reports found.
        </div>
      )}

      <div className="feedback-tab__list">
        {reports.map(report => {
          const statusInfo = STATUS_LABELS[report.status] || STATUS_LABELS.new;
          const isExpanded = expanded === report.id;

          return (
            <div
              key={report.id}
              className={`feedback-card ${isExpanded ? 'feedback-card--expanded' : ''}`}
              onClick={() => toggleExpand(report.id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.currentTarget !== e.target) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(report.id);
                }
              }}
            >
              <div className="feedback-card__row">
                <span className="feedback-card__category">
                  {CATEGORY_ICONS[report.category] || '📝'} {report.category}
                </span>
                <span className="feedback-card__id" title="Firestore document ID">
                  {report.id.slice(0, 8)}…
                </span>
                <span className={`feedback-card__status ${statusInfo.className}`}>
                  {statusInfo.label}
                </span>
                <span className="feedback-card__date">
                  {formatDate(report.createdAt)}
                </span>
              </div>

              <div className="feedback-card__message">
                {isExpanded ? report.message : report.message.slice(0, 120) + (report.message.length > 120 ? '…' : '')}
              </div>

              <div className="feedback-card__meta">
                <span>{report.email || report.uid?.slice(0, 8) || 'anonymous'}</span>
                {report.deviceInfo?.appVersion && (
                  <span className="feedback-card__badge" title={`${report.deviceInfo.gitCommit || ''} · ${report.deviceInfo.buildTimestamp || ''}`}>🏷️ v{report.deviceInfo.appVersion}{report.deviceInfo.gitCommit ? `@${report.deviceInfo.gitCommit}` : ''}</span>
                )}
                {report.deviceInfo?.environment && (
                  <span className="feedback-card__badge" title="Deployment environment">🌐 {report.deviceInfo.environment}</span>
                )}
                {report.deviceInfo?.authProvider && report.deviceInfo.authProvider !== 'unknown' && (
                  <span className="feedback-card__badge" title="Auth provider">🔑 {report.deviceInfo.authProvider}</span>
                )}
                {report.hasScreenshot && <span className="feedback-card__badge">📸 Screenshot</span>}
                {report.hasConsoleLogs && <span className="feedback-card__badge">📋 Logs</span>}
              </div>

              {isExpanded && (
                <div className="feedback-card__actions" onClick={e => e.stopPropagation()} role="presentation">
                  <span className="feedback-card__full-id" title="Click to copy">
                    ID: <code
                      onClick={async e => {
                        e.stopPropagation();
                        try { await navigator.clipboard.writeText(report.id); }
                        catch { /* clipboard unavailable */ }
                      }}
                      style={{ cursor: 'pointer' }}
                    >{report.id}</code>
                  </span>
                  <select
                    className="feedback-card__status-select"
                    value={report.status}
                    onChange={e => updateStatus(report.id, e.target.value)}
                    disabled={actionLoading.has(report.id)}
                  >
                    <option value="new">New</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                  <button
                    className="feedback-card__delete"
                    onClick={e => { e.stopPropagation(); deleteReport(report.id); }}
                    disabled={actionLoading.has(report.id)}
                    aria-label={`Delete feedback ${report.id}`}
                  >
                    🗑️ Delete
                  </button>
                </div>
              )}

              {isExpanded && report.deviceInfo && (
                <div className="feedback-card__device">
                  {report.deviceInfo.appVersion && (
                    <div><strong>App Version:</strong> v{report.deviceInfo.appVersion}{report.deviceInfo.gitCommit ? `@${report.deviceInfo.gitCommit}` : ''}{report.deviceInfo.buildTimestamp ? ` · ${report.deviceInfo.buildTimestamp}` : ''}</div>
                  )}
                  {report.deviceInfo.environment && <div><strong>Environment:</strong> {report.deviceInfo.environment}</div>}
                  {report.deviceInfo.apiBaseUrl && <div><strong>API:</strong> {report.deviceInfo.apiBaseUrl}</div>}
                  {report.deviceInfo.authProvider && report.deviceInfo.authProvider !== 'unknown' && (
                    <div><strong>Auth:</strong> {report.deviceInfo.authProvider}{report.deviceInfo.accountCreated ? ` (since ${report.deviceInfo.accountCreated})` : ''}</div>
                  )}
                  <div><strong>Platform:</strong> {report.deviceInfo.platform}</div>
                  <div><strong>Screen:</strong> {report.deviceInfo.screenWidth}×{report.deviceInfo.screenHeight}</div>
                  {(report.deviceInfo.viewportWidth || report.deviceInfo.viewportHeight) && (
                    <div><strong>Viewport:</strong> {report.deviceInfo.viewportWidth}×{report.deviceInfo.viewportHeight}</div>
                  )}
                  <div><strong>Page:</strong> {report.deviceInfo.url}</div>
                  {report.deviceInfo.currentRoute && <div><strong>Route:</strong> {report.deviceInfo.currentRoute}</div>}
                  <div><strong>Language:</strong> {report.deviceInfo.language}{report.deviceInfo.locale && report.deviceInfo.locale !== report.deviceInfo.language ? ` (locale: ${report.deviceInfo.locale})` : ''}</div>
                  {report.deviceInfo.timezone && <div><strong>Timezone:</strong> {report.deviceInfo.timezone}</div>}
                  {report.deviceInfo.theme && <div><strong>Theme:</strong> {report.deviceInfo.theme}</div>}
                  {report.deviceInfo.connectionType && <div><strong>Network:</strong> {report.deviceInfo.connectionType}{report.deviceInfo.online === false ? ' (offline)' : ''}</div>}
                  <div><strong>User Agent:</strong> {report.deviceInfo.userAgent}</div>
                </div>
              )}

              {isExpanded && detailLoading === report.id && (
                <div className="feedback-card__detail-loading">Loading details…</div>
              )}

              {isExpanded && details[report.id] && (
                <div
                  role="presentation"
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                >
                  {/* Console Logs */}
                  {details[report.id].consoleLogs.length > 0 && (
                    <div className="feedback-card__section">
                      <strong>📋 Console Logs ({details[report.id].consoleLogs.length})</strong>
                      <pre className="feedback-card__logs">
                        {details[report.id].consoleLogs.join('\n')}
                      </pre>
                    </div>
                  )}

                  {/* Recent API Calls */}
                  {details[report.id].recentApiCalls?.length > 0 && (
                    <div className="feedback-card__section">
                      <strong>🌐 Recent API Calls ({details[report.id].recentApiCalls.length})</strong>
                      <pre className="feedback-card__logs">
                        {details[report.id].recentApiCalls.map(c =>
                          `${c.ts} ${c.method} ${c.url} → ${c.status ?? 'ERR'} (${c.durationMs}ms)${c.error ? ` [${c.error}]` : ''}`
                        ).join('\n')}
                      </pre>
                    </div>
                  )}

                  {/* Usage Snapshot */}
                  {details[report.id].usageSnapshot && (
                    <div className="feedback-card__section">
                      <strong>📊 Usage Snapshot</strong>
                      <pre className="feedback-card__logs">
                        {JSON.stringify(details[report.id].usageSnapshot, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Screenshot */}
                  {details[report.id].screenshot && (
                    <div className="feedback-card__section">
                      <strong>📸 Screenshot</strong>
                      <img
                        src={details[report.id].screenshot!}
                        alt="User screenshot"
                        className="feedback-card__screenshot"
                        onClick={e => { e.stopPropagation(); setLightboxSrc(details[report.id].screenshot!); }}
                        role="button"
                        tabIndex={0}
                        aria-label="View screenshot in lightbox"
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setLightboxSrc(details[report.id].screenshot!); } }}
                      />
                    </div>
                  )}

                  {/* Recent Chat Messages */}
                  {details[report.id].recentMessages.length > 0 && (
                    <div className="feedback-card__section">
                      <strong>💬 Recent Chat ({details[report.id].recentMessages.length})</strong>
                      <div className="feedback-card__messages">
                        {details[report.id].recentMessages.map((m, i) => {
                          const safeSender = m.sender === 'user' || m.sender === 'bot' ? m.sender : 'unknown';
                          return (
                            <div key={i} className={`feedback-card__msg feedback-card__msg--${safeSender}`}>
                              <span className="feedback-card__msg-sender">{safeSender}:</span> {m.text || (safeSender === 'user' ? '[Photo]' : '[empty]')}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && <div className="feedback-tab__loading">Loading…</div>}

      {hasMore && !loading && (
        <button
          className="feedback-tab__load-more"
          onClick={() => nextCursor && loadReports(nextCursor)}
        >
          Load More
        </button>
      )}

      {/* Screenshot lightbox */}
      {lightboxSrc && (
        <div
          className="feedback-lightbox"
          onClick={() => setLightboxSrc(null)}
          onKeyDown={e => { if (e.key === 'Escape') setLightboxSrc(null); }}
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot preview"
        >
          <button className="feedback-lightbox__close" onClick={() => setLightboxSrc(null)} aria-label="Close" autoFocus>✕</button>
          <img src={lightboxSrc} alt="Screenshot full view" className="feedback-lightbox__img" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default FeedbackTab;
